// Where an "Apply now" / "Pre-qualified today" button on a loan officer's
// personal site should send a borrower.
//
// The page used to build this URL in seven places, each a copy of the same
// three dead branches (zeitro.us, localhost and the default all returned the
// same string). One function now answers it for the whole site.

// Customers whose application buttons open the one-page application instead of
// the full pre-approval flow.
const ONE_PAGE_APPLICATION_CUSTOMERS = ['gmcc']

// GMCC's borrowers reach the one-page application on GMCC's own domain, so the
// address bar stays with the lender they are applying to. That domain only
// exists in production; the test environments serve the page themselves.
const onePageApplicationHost = () => {
    if (typeof window === 'undefined') return ''
    const host = window.location.host
    const isProduction = host === 'lo.zeitro.com' || host === 'agent.gmccloan.com'
    return isProduction ? 'https://agent.gmccloan.com' : window.location.origin
}

export const validatedURL = (link) => {
    try {
        if (!link) return null
        const url = new URL(/^https?:\/\//.test(link) ? link : `https://${link}`)
        return /^(?:localhost|[\w.-]+\.[a-z]{2,})$/i.test(url.hostname) ? url.href : null
    } catch (error) {
        console.error('Invalid URL string: ', link, error)
        return null
    }
}

/** The `{ isValid, url }` shape the page's other link checks already use. */
export const getValidatedURL = (link) => {
    const url = validatedURL(link)
    return url ? { isValid: true, url } : { isValid: false, url: null }
}

/**
 * Three answers in order of precedence:
 *   1. the one-page application, for customers that use it;
 *   2. the loan officer's own application URL;
 *   3. the pre-approval flow, which is what everyone had before.
 *
 * The one-page application comes first because the links loan officers had set
 * for themselves were mostly not applications at all -- of GMCC's 39, 32 were
 * their marketing page on gmccloan.com, three pointed back at the very page the
 * button is on, and one was an Encompass sign-in screen. Their links are left
 * untouched in the database, so putting them back in front is a one-line
 * change here.
 *
 * `leadsChannel` is only carried on the pre-approval URL; the one-page
 * application identifies its source from the link itself.
 */
export const getApplicationLink = ({
    customerid,
    loid,
    appLink,
    appUrl,
    leadsChannel,
} = {}) => {
    if (customerid && loid && ONE_PAGE_APPLICATION_CUSTOMERS.includes(customerid)) {
        const host = onePageApplicationHost()
        if (host) return `${host}/${customerid}/${loid}/onepage`
    }

    const own = validatedURL(appLink)
    if (own) return own

    if (!appUrl) return ''
    const params = [`customerid=${customerid}`]
    // A page that knows no loan officer sends none, rather than "undefined".
    if (loid) params.push(`loid=${loid}`)
    if (leadsChannel) params.push(`leadschannel=${leadsChannel}`)
    return `${appUrl}/services/preapp?${params.join('&')}`
}

export default getApplicationLink
